import dayjs from "dayjs";
import { useMemo } from "react";

import {
  Flight,
  FlightLeg,
} from "../../../../../store/slices/reservationSlice";
import { useTypedSelector } from "../../../../../store/store";

import styles from "./flight-item-card.module.scss";

const bySeqThenDate = (a: FlightLeg, b: FlightLeg) => {
  if (a.sequence !== b.sequence) {
    return a.sequence - b.sequence;
  }

  const ad = a.departure_datetime
    ? dayjs(a.departure_datetime).valueOf()
    : Infinity;

  const bd = b.departure_datetime
    ? dayjs(b.departure_datetime).valueOf()
    : Infinity;

  return ad - bd;
};

interface FlightItemCardProps {
  flight: Flight;
}

export const FlightItemCard = ({ flight }: FlightItemCardProps) => {
  const { date_format } = useTypedSelector((state) => state.environment);

  const { first, last } = useMemo(() => {
    const legs = Array.isArray(flight?.legs) ? [...flight.legs] : [];

    legs.sort(bySeqThenDate);

    const first = legs[0];
    const last = legs[legs.length - 1];

    return { first, last };
  }, [flight?.legs]);

  const dateLabel = useMemo(
    () =>
      first?.departure_datetime
        ? dayjs(first.departure_datetime).format(date_format)
        : "-",
    [first?.departure_datetime, date_format],
  );

  return (
    <div className={styles.card}>
      <div className={styles.card_header}>
        <span className={styles.card_headerDate}>{dateLabel}</span>

        <span
          className={
            styles.card_headerCode
          }>{`${first?.flight_operator ?? ""}${first?.flight_number ?? ""}`}</span>
      </div>

      <div className={styles.card_body}>
        <div className={styles.card_routeContainer}>
          <span className={styles.card_route}>
            {`${(first?.departure_airport_name || first?.departure_airport || "").toUpperCase()} - ${(last?.arrival_airport_name || last?.arrival_airport || "").toUpperCase()}`}
          </span>
        </div>

        <div className={styles.card_times}>
          <div className={styles.card_times_left}>
            <span className={styles.card_route} aria-hidden>
              ✈
            </span>

            {first?.departure_airport && (
              <span className={styles.card_route}>
                {first.departure_airport}
              </span>
            )}

            {first?.departure_datetime && (
              <span className={styles.card_route}>
                {dayjs(first.departure_datetime).format("h:mma")}
              </span>
            )}

            <span className={styles.card_route}>–</span>

            <span className={styles.card_route}>
              {last?.arrival_airport && (
                <span className={styles.card_route}>
                  {last.arrival_airport}
                </span>
              )}
            </span>

            {last?.arrival_datetime && (
              <span className={styles.card_route}>
                {dayjs(last.arrival_datetime).format("h:mma")}
              </span>
            )}
          </div>

          <div className={styles.card_times_right}>
            {first?.departure_datetime && last?.arrival_datetime ? (
              <span>
                {(() => {
                  const mins = dayjs(last.arrival_datetime).diff(
                    dayjs(first.departure_datetime),
                    "minute",
                  );

                  const h = Math.floor(mins / 60);
                  const m = mins % 60;

                  return `${h}h${m ? ` ${m}m` : ""} flight time`;
                })()}
              </span>
            ) : (
              <span>-</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
